// src/pages/hotel-bookings/AdminHotelBookingsPage.tsx
//
// Hotel bookings for operations. Same list/search/tab-count endpoints as tours
// and flights (itemType=HOTEL); each row carries the backend's `hotelSummary`.
//
// Deliberately no delete action: a hotel booking is a live supplier
// reservation, and deleting our row would not release it. Cancel it from the
// detail page instead, which goes through the supplier and refunds the wallet.

import type { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import TabBar from "@/components/shared/ui/TabBar";
import TablePagination from "@/components/shared/ui/TablePagination";

import {
  useAdminBookingsPage,
  type AdminBookingsColumnFilters,
  type BookingRow,
  type BookingTabKey,
} from "@/pages/bookings/hooks/useAdminBookingsPage";

import { BookingStatusPill, CancellationStatusPill } from "./components/hotelBookingUi";
import { formatDate } from "./utils/hotelBookingFormat";

const TABS: Array<{ key: BookingTabKey; label: string }> = [
  { key: "ALL", label: "All" },
  { key: "CONFIRMED", label: "Confirmed" },
  { key: "ON_HOLD", label: "On Hold" },
  { key: "CANCELLED", label: "Cancelled" },
];

const COLUMN_COUNT = 10;

export default function AdminHotelBookingsPage() {
  const navigate = useNavigate();

  const {
    tab,
    setTab,
    q,
    setQ,
    colFilters,
    setColFilters,
    page,
    setPage,
    perPage,
    totalPages,
    totalItems,
    serialStart,
    rows,
    tabCounts,
    isLoading,
    isError,
    refetch,
  } = useAdminBookingsPage(10, "HOTEL");

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Hotel Bookings</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search bookings…"
            aria-label="Search hotel bookings"
            className="w-72 rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
        </div>
      </div>

      <TabBar
        ariaLabel="Booking status"
        className="mb-4"
        activeKey={tab}
        onSelect={(key) => setTab(key as BookingTabKey)}
        items={TABS.map((t) => ({ key: t.key, label: t.label, count: tabCounts?.[t.key] ?? 0 }))}
      />

      {isError && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>Failed to load hotel bookings.</span>
          <button
            onClick={() => refetch()}
            className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      <div className="relative">
        <HotelBookingsTable
          rows={rows}
          serialStart={serialStart}
          isLoading={isLoading}
          colFilters={colFilters}
          setColFilters={setColFilters}
          onRowClick={(id) => navigate(`/hotel-bookings/${id}`)}
        />

        {isLoading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs text-gray-600 shadow-sm">
              Loading hotel bookings…
            </div>
          </div>
        )}
      </div>

      <TablePagination
        page={page}
        pageSize={perPage}
        total={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </AdminLayout>
  );
}

function HotelBookingsTable({
  rows,
  serialStart,
  isLoading,
  colFilters,
  setColFilters,
  onRowClick,
}: {
  rows: BookingRow[];
  serialStart: number;
  isLoading: boolean;
  colFilters: AdminBookingsColumnFilters;
  setColFilters: Dispatch<SetStateAction<AdminBookingsColumnFilters>>;
  onRowClick: (id: number) => void;
}) {
  return (
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">
      <div className="overflow-auto">
        <table className="w-full min-w-[1180px] text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-600">
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 text-left font-medium">#</th>
              <th className="px-3 py-2 text-left font-medium">
                <div className="flex items-center gap-1.5">
                  <span>Booking Code</span>
                  <ColumnFilter label="Booking Code">
                    <Input
                      placeholder="Contains…"
                      value={colFilters.bookingCode || ""}
                      onChange={(e) => setColFilters((f) => ({ ...f, bookingCode: e.target.value }))}
                    />
                  </ColumnFilter>
                </div>
              </th>
              <th className="px-3 py-2 text-left font-medium">
                <div className="flex items-center gap-1.5">
                  <span>Customer</span>
                  <ColumnFilter label="Customer">
                    <Input
                      placeholder="Name or email…"
                      value={colFilters.customer || ""}
                      onChange={(e) => setColFilters((f) => ({ ...f, customer: e.target.value }))}
                    />
                  </ColumnFilter>
                </div>
              </th>
              <th className="px-3 py-2 text-left font-medium">Hotel</th>
              <th className="px-3 py-2 text-left font-medium">Stay</th>
              <th className="px-3 py-2 text-left font-medium">Rooms / Guests</th>
              <th className="px-3 py-2 text-left font-medium">Supplier</th>
              <th className="px-3 py-2 text-left font-medium">Paid</th>
              <th className="px-3 py-2 text-left font-medium">Booked</th>
              <th className="px-3 py-2 text-left font-medium">Status</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-3 py-10 text-center text-gray-500">
                  {isLoading ? " " : "No hotel bookings found."}
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => {
                const h = r.hotel;
                const location = [h?.cityName, h?.countryName].filter(Boolean).join(", ");
                return (
                  <tr
                    key={r.id}
                    className="cursor-pointer border-b border-gray-100 hover:bg-sky-50/40"
                    onClick={() => onRowClick(r.id)}
                  >
                    <td className="px-3 py-3 text-gray-500">{serialStart + idx + 1}</td>

                    <td className="px-3 py-3">
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-900">{r.bookingCode || `#${r.id}`}</span>
                        <span className="text-xs text-gray-500">ID: {r.id}</span>
                      </div>
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{r.customerName || "—"}</span>
                        <span className="text-xs text-gray-500">{r.customerEmail || r.customerPhone || "—"}</span>
                      </div>
                    </td>

                    <td className="max-w-[240px] px-3 py-3">
                      <div className="flex flex-col">
                        <span className="truncate font-medium text-gray-900" title={h?.hotelName ?? undefined}>
                          {h?.hotelName || "—"}
                        </span>
                        <span className="truncate text-xs text-gray-500" title={h?.roomName ?? undefined}>
                          {location || h?.roomName || "—"}
                        </span>
                      </div>
                    </td>

                    <td className="px-3 py-3 text-gray-700">
                      <div className="flex flex-col">
                        <span>{formatDate(h?.checkIn ?? r.travelStartDate)}</span>
                        <span className="text-xs text-gray-500">
                          to {formatDate(h?.checkOut ?? r.travelEndDate)}
                          {h?.nights ? ` · ${h.nights}N` : ""}
                        </span>
                      </div>
                    </td>

                    <td className="px-3 py-3 text-gray-700">
                      {h ? `${h.rooms ?? 1} / ${h.guests || "—"}` : r.paxLabel}
                    </td>

                    <td className="px-3 py-3">
                      <div className="flex flex-col">
                        <span className="text-gray-900">{h?.supplier || "—"}</span>
                        <span className="text-xs text-gray-500">
                          {h?.confirmationNo ? `Conf: ${h.confirmationNo}` : "No confirmation no."}
                        </span>
                      </div>
                    </td>

                    <td className="px-3 py-3 font-medium text-gray-900">{r.paidTotalLabel}</td>

                    <td className="px-3 py-3 text-gray-700">{formatDate(r.createdAt)}</td>

                    <td className="px-3 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <BookingStatusPill status={r.status} />
                        {h?.cancellationStatus && h.cancellationStatus !== "CANCELLED" ? (
                          <CancellationStatusPill status={h.cancellationStatus} />
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
